import { Box, Theme, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { BudgetCapping, DailyBudgetType } from '../../../../../__generated__/graphql'
import { StatsConf, valueFormatter } from '../../../../../components/stats/statsConf'
import { useAppTranslations } from '../../../../../utils/hooks/useAppTranslations'
import { getActivityFeedPropertyLabel } from '../../../../activity/activityFeedDisplay'
import {
  BudgetHistoryChartPoint,
  BudgetHistoryFallbacks,
  applyBrokenAxisOpenBudgetBars,
  buildBudgetHistoryChartSeries,
  BUDGET_HISTORY_Y_AXIS_DOMAIN,
} from './budgetHistoryChartSeries'
import { formatBudgetHistoryChangeAuthor } from './budgetHistoryChangeAuthor'
import { BUDGET_HISTORY_TIMEFRAME } from './budgetHistoryRange'
import { BudgetHistoryBreakDotsLayer, BudgetHistoryBudgetYAxisTick } from './BudgetHistoryAxisBreakMarker'
import {
  applyBrokenBudgetAxisToPoint,
  BROKEN_BUDGET_AXIS_BREAK_TICK,
  BROKEN_BUDGET_AXIS_TICKS,
  BUDGET_DISPLAY_DOMAIN,
  BrokenBudgetAxisConfig,
  formatBrokenBudgetAxisTick,
  getBrokenBudgetAxisConfig,
  getBudgetAxisMode,
  getBudgetLogAxisDomain,
} from './budgetHistoryBudgetAxis'
import { AdGroupBudgetHistoryEntry } from './budgetHistoryTypes'

type BudgetHistoryCostChartProps = {
  changes: AdGroupBudgetHistoryEntry[]
  fallbacks: BudgetHistoryFallbacks
  dense?: boolean
}

const costStatStyle = StatsConf.costs!
const BAR_FILL_OPACITY = 0.25
const BAR_CATEGORY_GAP_PERCENT = 8
const BAR_CATEGORY_GAP = `${BAR_CATEGORY_GAP_PERCENT}%`
// A gap of minus one bar width stacks every bar series in the same full-width slot.
const BAR_GAP = `-${100 - 2 * BAR_CATEGORY_GAP_PERCENT}%`
const Y_AXIS_BUDGET = 'budget'
const Y_AXIS_COST = 'cost'
const Y_AXIS_INTERACTION = 'interaction'
const Y_AXIS_WIDTH = 64
const Y_AXIS_PADDING = { top: 16, bottom: 0 }
const X_AXIS_HEIGHT = 30
const CHART_BODY_HEIGHT_DENSE = 280
const CHART_BODY_HEIGHT = 320

const chartMargin = (
  showCostAxis: boolean,
  showBudgetAxis: boolean
): { top: number; right: number; left: number; bottom: number } => ({
  top: 12,
  right: showCostAxis ? 16 : 8,
  left: showBudgetAxis ? 16 : 8,
  bottom: 8,
})

const formatAxisCurrency = (value: number): string => valueFormatter(costStatStyle, value)

const formatYAxisTick = (value: number): string => (value === 0 ? '' : formatAxisCurrency(value))

export type BudgetHistorySeriesGroup = 'cost' | 'dailyBudget' | 'budgetCapping'

export type BudgetHistorySeriesVisibility = Record<BudgetHistorySeriesGroup, boolean>

const DEFAULT_SERIES_VISIBILITY: BudgetHistorySeriesVisibility = {
  cost: true,
  dailyBudget: true,
  budgetCapping: true,
}

type SeriesConfig = {
  dataKey: keyof BudgetHistoryChartPoint
  group: BudgetHistorySeriesGroup
  kind: 'line' | 'bar'
  color: string
  label: string
  yAxisId: typeof Y_AXIS_BUDGET | typeof Y_AXIS_COST
}

type LegendRow = {
  color: string
  text: string
}

const useBudgetHistoryTypeLabels = (): {
  dailyBudgetTypeLabels: Record<DailyBudgetType, string>
  budgetCappingTypeLabels: Record<BudgetCapping, string>
} => {
  const t = useAppTranslations()
  return useMemo(
    () => ({
      dailyBudgetTypeLabels: {
        [DailyBudgetType.Open]: t('adgroup.budgetAndDates.budgetType.Open'),
        [DailyBudgetType.Fixed]: t('adgroup.budgetAndDates.budgetType.Fixed'),
        [DailyBudgetType.Auto]: t('adgroup.budgetAndDates.budgetType.Auto'),
      },
      budgetCappingTypeLabels: {
        [BudgetCapping.Open]: t('enum.BudgetCapping.Open'),
        [BudgetCapping.Monthly]: t('enum.BudgetCapping.Monthly'),
        [BudgetCapping.Period]: t('enum.BudgetCapping.Period'),
        [BudgetCapping.Overall]: t('enum.BudgetCapping.Overall'),
      },
    }),
    [t]
  )
}

const overallCappingColor = (theme: Theme): string =>
  theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[700]

const buildLegendRows = (
  point: BudgetHistoryChartPoint | undefined,
  t: ReturnType<typeof useAppTranslations>,
  typeLabels: ReturnType<typeof useBudgetHistoryTypeLabels>,
  visibility: BudgetHistorySeriesVisibility,
  theme: Theme
): LegendRow[] => {
  if (!point) return []

  const costLabel = getActivityFeedPropertyLabel('costValue', t)
  const dailyLabel = getActivityFeedPropertyLabel('dailyBudget', t)
  const cappingLabel = getActivityFeedPropertyLabel('budgetCapping', t)

  const rows: LegendRow[] = []

  if (visibility.cost && (point.costValue != null || point.costType != null)) {
    const costTypeLabel = point.costType?.toUpperCase() ?? t('commons.noValueYet')
    const value = point.costValue != null ? valueFormatter(costStatStyle, point.costValue) : t('commons.noValueYet')
    rows.push({
      color: theme.palette.error.main,
      text: t('adgroup.spentBudget.history.chartLegendCost', { label: costLabel, costType: costTypeLabel, value }),
    })
  }

  if (visibility.dailyBudget && point.dailyBudgetType != null) {
    const color =
      point.dailyBudgetType === DailyBudgetType.Open
        ? theme.palette.primary.main
        : point.dailyBudgetType === DailyBudgetType.Fixed
          ? theme.palette.info.main
          : theme.palette.success.main
    const dailyValue =
      point.dailyBudgetType !== DailyBudgetType.Fixed
        ? typeLabels.dailyBudgetTypeLabels[point.dailyBudgetType]
        : point.dailyBudgetAmount != null
          ? valueFormatter(costStatStyle, point.dailyBudgetAmount)
          : t('commons.noValueYet')
    rows.push({
      color,
      text: t('adgroup.spentBudget.history.chartLegendValue', { label: dailyLabel, value: dailyValue }),
    })
  }

  if (visibility.budgetCapping && point.budgetCappingType != null) {
    const typeLabel = typeLabels.budgetCappingTypeLabels[point.budgetCappingType]
    const cappingColors: Record<BudgetCapping, string> = {
      [BudgetCapping.Open]: theme.palette.secondary.main,
      [BudgetCapping.Monthly]: theme.palette.warning.main,
      [BudgetCapping.Period]: '#F7915D',
      [BudgetCapping.Overall]: overallCappingColor(theme),
    }
    rows.push({
      color: cappingColors[point.budgetCappingType],
      text:
        point.budgetCappingType === BudgetCapping.Open
          ? t('adgroup.spentBudget.history.chartLegendTypeOnly', { label: cappingLabel, type: typeLabel })
          : t('adgroup.spentBudget.history.chartLegendCapping', {
              label: cappingLabel,
              type: typeLabel,
              value:
                point.budgetCappingAmount != null
                  ? valueFormatter(costStatStyle, point.budgetCappingAmount)
                  : t('commons.noValueYet'),
            }),
    })
  }

  return rows
}

const resolveTooltipPoint = (
  payload: Array<{ payload?: BudgetHistoryChartPoint }> | undefined,
  label: string | undefined,
  chartData: BudgetHistoryChartPoint[]
): BudgetHistoryChartPoint | undefined => {
  const fromPayload = payload?.find((entry) => entry.payload != null)?.payload
  if (fromPayload) return fromPayload
  if (label) return chartData.find((point) => point.label === label)
  return undefined
}

const ChartTooltip: React.FC<{
  active?: boolean
  payload?: Array<{ payload?: BudgetHistoryChartPoint }>
  label?: string
  chartData: BudgetHistoryChartPoint[]
  visibility: BudgetHistorySeriesVisibility
}> = ({ active, payload, label, chartData, visibility }) => {
  const t = useAppTranslations()
  const theme = useTheme()
  const typeLabels = useBudgetHistoryTypeLabels()
  const point = resolveTooltipPoint(payload, label, chartData)
  const rows = buildLegendRows(point, t, typeLabels, visibility, theme)

  const changedBy = formatBudgetHistoryChangeAuthor(t, point?.changeAuthor)

  if (!active || !point) return null
  if (rows.length === 0 && changedBy == null) return null

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 1,
        py: 0.5,
        typography: 'caption',
        maxWidth: 320,
      }}
    >
      <Box sx={{ fontWeight: 600, mb: 0.5 }}>{label}</Box>
      {rows.map((row) => (
        <Box key={row.text} sx={{ color: row.color }}>
          {row.text}
        </Box>
      ))}
      {changedBy ? (
        <Box sx={{ color: 'text.secondary', mt: rows.length > 0 ? 0.5 : 0 }}>
          {t('adgroup.spentBudget.history.changedBy', { name: changedBy })}
        </Box>
      ) : null}
    </Box>
  )
}

const BudgetHistorySeriesToggles: React.FC<{
  visibility: BudgetHistorySeriesVisibility
  onVisibilityChange: (visibility: BudgetHistorySeriesVisibility) => void
  dense: boolean
}> = ({ visibility, onVisibilityChange, dense }) => {
  const t = useAppTranslations()
  const selectedGroups = (Object.keys(visibility) as BudgetHistorySeriesGroup[]).filter((group) => visibility[group])

  const handleChange = (_event: React.MouseEvent<HTMLElement>, newSelection: BudgetHistorySeriesGroup[]): void => {
    onVisibilityChange({
      cost: newSelection.includes('cost'),
      dailyBudget: newSelection.includes('dailyBudget'),
      budgetCapping: newSelection.includes('budgetCapping'),
    })
  }

  const toggleOptions: Array<{ group: BudgetHistorySeriesGroup; label: string }> = [
    {
      group: 'cost',
      label: getActivityFeedPropertyLabel('costValue', t),
    },
    {
      group: 'dailyBudget',
      label: getActivityFeedPropertyLabel('dailyBudget', t),
    },
    {
      group: 'budgetCapping',
      label: getActivityFeedPropertyLabel('budgetCapping', t),
    },
  ]

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 1 }}>
      <ToggleButtonGroup
        size={dense ? 'small' : 'medium'}
        value={selectedGroups}
        onChange={handleChange}
        aria-label={t('adgroup.spentBudget.history.seriesToggleLabel')}
        sx={{ flexWrap: 'wrap', justifyContent: 'center' }}
      >
        {toggleOptions.map((option) => (
          <ToggleButton key={option.group} value={option.group} sx={{ textTransform: 'none', py: dense ? 0.25 : 0.5 }}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  )
}

export const BudgetHistoryCostChart: React.FC<BudgetHistoryCostChartProps> = ({
  changes,
  fallbacks,
  dense = false,
}) => {
  const t = useAppTranslations()
  const theme = useTheme()
  const typeLabels = useBudgetHistoryTypeLabels()
  const [seriesVisibility, setSeriesVisibility] = useState<BudgetHistorySeriesVisibility>(DEFAULT_SERIES_VISIBILITY)
  const chartData = useMemo(
    () => buildBudgetHistoryChartSeries(changes, BUDGET_HISTORY_TIMEFRAME, fallbacks),
    [changes, fallbacks]
  )

  const seriesConfig = useMemo((): SeriesConfig[] => {
    const { dailyBudgetTypeLabels, budgetCappingTypeLabels } = typeLabels

    return [
      {
        dataKey: 'costValue',
        group: 'cost',
        kind: 'line',
        color: theme.palette.error.main,
        label: t('activity.feed.properties.costValue'),
        yAxisId: Y_AXIS_COST,
      },
      {
        dataKey: 'dailyBudget_Open',
        group: 'dailyBudget',
        kind: 'bar',
        color: theme.palette.primary.main,
        label: dailyBudgetTypeLabels[DailyBudgetType.Open],
        yAxisId: Y_AXIS_BUDGET,
      },
      {
        dataKey: 'dailyBudget_Fixed',
        group: 'dailyBudget',
        kind: 'line',
        color: theme.palette.info.main,
        label: dailyBudgetTypeLabels[DailyBudgetType.Fixed],
        yAxisId: Y_AXIS_BUDGET,
      },
      {
        dataKey: 'dailyBudget_Auto',
        group: 'dailyBudget',
        kind: 'bar',
        color: theme.palette.success.main,
        label: dailyBudgetTypeLabels[DailyBudgetType.Auto],
        yAxisId: Y_AXIS_BUDGET,
      },
      {
        dataKey: 'budgetCapping_Open',
        group: 'budgetCapping',
        kind: 'bar',
        color: theme.palette.secondary.main,
        label: budgetCappingTypeLabels[BudgetCapping.Open],
        yAxisId: Y_AXIS_BUDGET,
      },
      {
        dataKey: 'budgetCapping_Monthly',
        group: 'budgetCapping',
        kind: 'line',
        color: theme.palette.warning.main,
        label: budgetCappingTypeLabels[BudgetCapping.Monthly],
        yAxisId: Y_AXIS_BUDGET,
      },
      {
        dataKey: 'budgetCapping_Period',
        group: 'budgetCapping',
        kind: 'line',
        color: '#F7915D',
        label: budgetCappingTypeLabels[BudgetCapping.Period],
        yAxisId: Y_AXIS_BUDGET,
      },
      {
        dataKey: 'budgetCapping_Overall',
        group: 'budgetCapping',
        kind: 'line',
        color: overallCappingColor(theme),
        label: budgetCappingTypeLabels[BudgetCapping.Overall],
        yAxisId: Y_AXIS_BUDGET,
      },
    ]
  }, [t, theme, typeLabels])

  const visibleSeries = useMemo(
    () =>
      seriesConfig.filter(
        (series) =>
          seriesVisibility[series.group] &&
          chartData.some((point) => {
            const value = point[series.dataKey]
            return value != null && Number.isFinite(value as number)
          })
      ),
    [chartData, seriesConfig, seriesVisibility]
  )

  const chartHeight = (dense ? CHART_BODY_HEIGHT_DENSE : CHART_BODY_HEIGHT) + X_AXIS_HEIGHT
  const showCostAxis = seriesVisibility.cost
  const showBudgetAxis = seriesVisibility.dailyBudget || seriesVisibility.budgetCapping
  const budgetAxisMode = useMemo(
    () => getBudgetAxisMode(chartData, seriesVisibility.dailyBudget, seriesVisibility.budgetCapping),
    [chartData, seriesVisibility.budgetCapping, seriesVisibility.dailyBudget]
  )
  const brokenBudgetAxisConfig = useMemo((): BrokenBudgetAxisConfig | undefined => {
    if (budgetAxisMode !== 'broken') return undefined
    return getBrokenBudgetAxisConfig(chartData)
  }, [budgetAxisMode, chartData])
  const plotChartData = useMemo(() => {
    if (budgetAxisMode !== 'broken' || brokenBudgetAxisConfig == null) return chartData
    return chartData.map((point) => {
      const transformed = applyBrokenBudgetAxisToPoint(point, brokenBudgetAxisConfig)
      return applyBrokenAxisOpenBudgetBars(transformed)
    })
  }, [brokenBudgetAxisConfig, budgetAxisMode, chartData])
  const gridYAxisId = showBudgetAxis ? Y_AXIS_BUDGET : showCostAxis ? Y_AXIS_COST : Y_AXIS_INTERACTION
  const showInteractionBand = chartData.length > 0
  const budgetAxisTickFormatter = (value: number): string => {
    if (budgetAxisMode === 'broken' && brokenBudgetAxisConfig != null) {
      return formatBrokenBudgetAxisTick(value, brokenBudgetAxisConfig, formatAxisCurrency)
    }
    return formatYAxisTick(value)
  }

  return (
    <Box sx={{ mb: 1.5, width: '100%', minWidth: 0, overflow: 'visible' }}>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart
          data={plotChartData}
          margin={chartMargin(showCostAxis, showBudgetAxis)}
          barGap={BAR_GAP}
          barCategoryGap={BAR_CATEGORY_GAP}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} yAxisId={gridYAxisId} syncWithTicks />
          <XAxis
            yAxisId={gridYAxisId}
            dataKey="label"
            height={X_AXIS_HEIGHT}
            tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
            interval="preserveEnd"
            minTickGap={16}
            tickMargin={8}
            padding="no-gap"
            axisLine={false}
            tickLine={false}
          />
          {showBudgetAxis ? (
            <YAxis
              yAxisId={Y_AXIS_BUDGET}
              orientation="left"
              scale={budgetAxisMode === 'broken' ? 'linear' : 'log'}
              domain={budgetAxisMode === 'broken' ? BUDGET_DISPLAY_DOMAIN : getBudgetLogAxisDomain(chartData)}
              allowDataOverflow={budgetAxisMode !== 'broken'}
              ticks={budgetAxisMode === 'broken' ? BROKEN_BUDGET_AXIS_TICKS : undefined}
              tick={
                budgetAxisMode === 'broken'
                  ? (tickProps) => (
                      <BudgetHistoryBudgetYAxisTick
                        {...tickProps}
                        breakDisplayValue={BROKEN_BUDGET_AXIS_BREAK_TICK}
                        formatTick={budgetAxisTickFormatter}
                        tickFill={theme.palette.text.secondary}
                      />
                    )
                  : { fontSize: 10, fill: theme.palette.text.secondary }
              }
              width={Y_AXIS_WIDTH}
              tickMargin={8}
              padding={Y_AXIS_PADDING}
              axisLine={false}
              tickLine={false}
              tickFormatter={budgetAxisTickFormatter}
            />
          ) : null}
          {showCostAxis ? (
            <YAxis
              yAxisId={Y_AXIS_COST}
              orientation="right"
              scale="linear"
              domain={BUDGET_HISTORY_Y_AXIS_DOMAIN}
              tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
              width={Y_AXIS_WIDTH}
              tickMargin={8}
              padding={Y_AXIS_PADDING}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatYAxisTick}
            />
          ) : null}
          {showInteractionBand ? (
            <YAxis yAxisId={Y_AXIS_INTERACTION} orientation="left" hide domain={BUDGET_HISTORY_Y_AXIS_DOMAIN} />
          ) : null}
          <Tooltip content={<ChartTooltip chartData={chartData} visibility={seriesVisibility} />} />
          {showInteractionBand ? (
            <Bar
              dataKey="interactionBand"
              yAxisId={Y_AXIS_INTERACTION}
              fill="transparent"
              stroke="none"
              isAnimationActive={false}
            />
          ) : null}
          {visibleSeries.map((series) =>
            series.kind === 'bar' ? (
              <Bar
                key={series.dataKey}
                yAxisId={series.yAxisId}
                dataKey={series.dataKey}
                name={series.label}
                fill={series.color}
                fillOpacity={BAR_FILL_OPACITY}
                stroke="none"
                baseValue={0}
                isAnimationActive={false}
                stackId={series.dataKey}
              />
            ) : (
              <Line
                key={series.dataKey}
                yAxisId={series.yAxisId}
                type="stepAfter"
                dataKey={series.dataKey}
                name={series.label}
                stroke={series.color}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
                connectNulls={false}
              />
            )
          )}
          {budgetAxisMode === 'broken' && showBudgetAxis && brokenBudgetAxisConfig != null ? (
            <BudgetHistoryBreakDotsLayer yAxisId={Y_AXIS_BUDGET} tickFill={theme.palette.text.secondary} />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>
      <BudgetHistorySeriesToggles
        visibility={seriesVisibility}
        onVisibilityChange={setSeriesVisibility}
        dense={dense}
      />
    </Box>
  )
}
